import { useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Chip,
  debounce,
  ListItem,
  Stack,
  TextField,
} from '@mui/material';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { useNotification } from '@/context/notification';
import { useSkillSearch } from '@/data/taxonomy/skills-search';
import { useEditUserPrioritySkillSets } from '@/data/taxonomy/user-priority-skill-sets';
import { SkillLabel, type Skill } from '@/models/taxonomy/skills';
import { useSkills } from '@/packages/skill-management/context/skills';
import type { CheckedSkill } from '@/packages/skill-management/context/types';

export type ManualSkillsDialogProps = {
  onClose: () => void;
};

export const ManualSkillsDialog = ({ onClose }: ManualSkillsDialogProps) => {
  const { manuallyAddedSkills, selectedSkills } = useSkills();
  const { mutate: updateSkills, isPending } = useEditUserPrioritySkillSets();
  const { toast } = useNotification();

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSetTerm = useMemo(
    () => debounce(setSearchTerm, 500),
    [setSearchTerm]
  );
  const [localSkills, setLocalSkills] = useState<CheckedSkill[]>(
    manuallyAddedSkills ?? []
  );

  const { options: searchedSkills, isLoading: isSearchingSkill } =
    useSkillSearch(searchTerm);

  const addSkill = (skill: Skill) => {
    const exists = localSkills.some((s) => s.skill.id === skill.id);
    if (!exists) {
      const newSkill: CheckedSkill = {
        id: skill.id,
        score: 1,
        label: SkillLabel.ManuallyAdded,
        skill: { id: skill.id, name_pt: skill.name_pt, name_en: skill.name_en },
        checked: true,
      };
      setLocalSkills((prev) => [...prev, newSkill]);
      setInputValue('');
      debouncedSetTerm.clear();
      setSearchTerm('');
    }
  };

  const removeSkill = (skillId: number) => {
    setLocalSkills((prev) => prev.filter((s) => s.skill.id !== skillId));
  };

  const skillsToUpdate = useMemo(() => {
    const skillsFromOtherSources = (selectedSkills || [])?.filter((skill) => {
      const isManualSkill = manuallyAddedSkills?.some(
        (manual) => manual.skill.id === skill.skill.id
      );

      return !isManualSkill;
    });

    return [...localSkills, ...skillsFromOtherSources];
  }, [localSkills, selectedSkills, manuallyAddedSkills]);

  const handleConfirm = () => {
    updateSkills(skillsToUpdate, {
      onSuccess: () => {
        toast({
          content: 'Suas habilidades foram atualizadas!',
          type: 'success',
        });
        // GlobalProfileStateEmitter.startRecommendationsLongPolling();
        onClose();
      },
      onError: () => {
        toast({
          content: 'Ocorreu um erro ao atualizar suas habilidades.',
          type: 'error',
        });
      },
    });
  };

  return (
    <ConfirmDialog
      open
      title="Seleção de habilidades"
      onClose={onClose}
      onConfirm={handleConfirm}
      disableConfirmButton={isPending}
      maxWidth="md"
    >
      <Stack gap={3} my={2}>
        <Autocomplete
          value={null}
          onChange={(_, newValue: Skill | null) => {
            if (newValue) addSkill(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
            debouncedSetTerm(newInputValue);
          }}
          options={searchedSkills}
          loading={isSearchingSkill}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Digite para buscar habilidades"
            />
          )}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              {option.name_pt}
            </ListItem>
          )}
          getOptionLabel={(option) => option.name_pt}
          filterOptions={(options) => options}
          noOptionsText={
            searchTerm.length === 0 || isSearchingSkill
              ? ''
              : 'Nenhum resultado no momento. Talvez a habilidade esteja escrita de outra forma.'
          }
          loadingText="Buscando habilidades..."
        />

        {localSkills.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {localSkills.map((skill) => (
              <Chip
                key={skill.skill.id}
                label={skill.skill.name_pt}
                onDelete={() => removeSkill(skill.skill.id)}
                variant="outlined"
                deleteIcon={<ClearIcon />}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </ConfirmDialog>
  );
};
